/**
 * « Fiches de révision » d'un élève — bloc partagé par la page élève du PROF et la
 * page enfants du PARENT (RPC `get_revision_sheet_packs`, 0744).
 *
 * N'affiche que les packs DÉJÀ ABORDÉS (jusqu'au pack en cours), groupés par matière,
 * le plus récent d'abord : un niveau de Terminale compte plus de 50 packs, et un pack
 * à venir n'a rien à réviser. Au-delà de `VISIBLE`, un bouton déplie le reste.
 *
 * Le lien porte `?student=` : le prof ou le parent voit la fiche TELLE QUE L'ÉLÈVE LA
 * VOIT, cartes non débloquées comprises. Regarder ne débloque rien.
 */

import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { FileText } from 'lucide-react';
import { subjectLabel } from '@jeraisonne/db/curriculum';
import {
  coveredPacksBySubject,
  useRevisionSheetPacks,
  type RevisionSheetPack,
} from '@/lib/cards/pack-revision-sheet';

/** Packs montrés d'emblée par matière ; le reste est derrière « + N autres ». */
const VISIBLE = 4;

function PackLink({ pack, studentId }: { pack: RevisionSheetPack; studentId: string }) {
  return (
    <Link
      to="/pack/$packId/fiche"
      params={{ packId: pack.id }}
      search={{ student: studentId }}
      className="border-ink-200 text-ink-700 hover:border-primary-300 hover:text-primary-700 inline-flex min-h-[2.75rem] items-center gap-1.5 rounded-full border bg-white px-3 text-xs font-semibold"
    >
      <FileText className="h-3.5 w-3.5 shrink-0" aria-hidden />
      <span className="max-w-[14rem] truncate">{pack.title}</span>
    </Link>
  );
}

function SubjectRow({
  subject,
  packs,
  studentId,
}: {
  subject: string;
  packs: readonly RevisionSheetPack[];
  studentId: string;
}) {
  const [all, setAll] = useState(false);
  const shown = all ? packs : packs.slice(0, VISIBLE);
  const hidden = packs.length - shown.length;
  return (
    <li className="space-y-1.5">
      <p className="text-ink-500 text-[11px] font-bold uppercase tracking-[.08em]">
        {subjectLabel(subject)}
      </p>
      <ul className="flex flex-wrap gap-1.5">
        {shown.map((pack) => (
          <li key={pack.id}>
            <PackLink pack={pack} studentId={studentId} />
          </li>
        ))}
        {hidden > 0 && (
          <li>
            <button
              type="button"
              onClick={() => setAll(true)}
              className="text-ink-500 hover:text-ink-800 min-h-[2.75rem] px-2 text-xs font-semibold underline"
            >
              + {hidden} autre{hidden > 1 ? 's' : ''}
            </button>
          </li>
        )}
      </ul>
    </li>
  );
}

export function RevisionSheetLinks({
  studentId,
  title = 'Fiches de révision',
  hint = 'vue de l’élève',
}: {
  studentId: string;
  title?: string;
  hint?: string;
}) {
  const packs = useRevisionSheetPacks(studentId);
  const groups = coveredPacksBySubject(packs.data ?? []);
  if (packs.isLoading || packs.error || groups.length === 0) return null;

  return (
    <section className="mb-3" aria-label={title}>
      <p className="text-ink-500 mb-1.5 text-xs font-semibold">
        {title} <span className="font-normal">({hint})</span>
      </p>
      <ul className="space-y-2">
        {groups.map((g) => (
          <SubjectRow key={g.subject} subject={g.subject} packs={g.packs} studentId={studentId} />
        ))}
      </ul>
    </section>
  );
}
